import {curry} from 'ramda';
import {History} from 'history';
import wrap from 'wraptile';

import {OnInputChangeEvent} from '@pluto/@types';
import {SetUsersAction, UserListFilterParams} from '@pluto/users';
import {
    PagingParams,
    SetPage,
    SetRowsPerPage,
} from '@pluto/table';
import {getNewSort} from '@pluto/qc-sort';
import {UserListFiltersHandlers} from './UsersListTypes';

export type HandleDebounceHandlers = {
    debouncedFunction: (value: string) => void;
    setState: (value: string) => void;
}

export interface HandleMountParams {
    getList: SetUsersAction;
    resetList?: () => void;
    setPage: SetPage;
    setRowsPerPage: SetRowsPerPage;
    params: UserListFilterParams;
    query: URLSearchParams;
}

interface QueryArgs {
    getList: SetUsersAction;
    setQuery: (history: History, route: string, params: UserListFilterParams) => void;
    history: History;
    route: string;
}

export const handleDebounce = curry((handlers: HandleDebounceHandlers, e: OnInputChangeEvent) => {
    const {value} = e.target;
    const {
        setState,
        debouncedFunction,
    } = handlers;
    
    setState(value);
    debouncedFunction(value);
});

export const handleDebouncedCallback = curry((handlers, newValue) => {
    const {
        getList,
        setPage,
        setQuery,
        history,
        route,
        key,
        ...params
    } = handlers;
    const newQuery = {
        ...params,
        [key]: newValue,
    };
    
    setPage(0);
    setQuery(history, route, newQuery);
    getList(newQuery);
});

export const handlePaging = curry((filters: UserListFilterParams, queryArgs: QueryArgs, tablePaging: PagingParams): void => {
    const {
        getList,
        setQuery,
        history,
        route,
    } = queryArgs;
    const params = {
        ...filters,
        ...tablePaging,
    };
    
    setQuery(history, route, params);
    getList(params);
});

export const handleMount = wrap((props: HandleMountParams) => {
    const {
        getList,
        resetList,
        setPage,
        setRowsPerPage,
        params,
        query,
    } = props;
    
    const queryLimit: string | null = query.get('limit');
    
    if (queryLimit) {
        const queryOffset: string | null = query.get('offset');
        const limit = Number(queryLimit);
        const page: number = Number(queryOffset) / limit || 0;
        
        setRowsPerPage(limit);
        setPage(page);
        getList({
            ...params,
            limit: Number(queryLimit),
        });
        return;
    }
    
    getList({
        ...params,
        limit: params.limit,
    });
    
    return resetList;
});

export const handleSort = curry((componentParams, oldSort) => {
    const {
        setSort,
        getList,
        setQuery,
        history,
        params,
        route,
    } = componentParams;
    
    const newSort = getNewSort(oldSort);
    setSort(newSort);
    
    const newQuery = {
        ...params,
        sortBy: newSort.sortBy,
        sortOrder: newSort.sortOrder,
    };
    
    setQuery(history, route, newQuery);
    getList(newQuery);
});

export const filterSelected = curry((name: string, element: string): boolean => element !== name);

export const setNewFilterValue = ({target}: OnInputChangeEvent, state: string[] = []): string[] => {
    const {name} = target;
    
    if (state.length === 0)
        return [name];
    
    if (state.includes(name)) {
        const withNameFilter = filterSelected(name);
        return state.filter(withNameFilter);
    }
    
    return [...state, name];
};

export const handleFilters = curry((filterHandlers: UserListFiltersHandlers, event: OnInputChangeEvent) => {
    const {
        setFilters,
        setPage,
        setQuery,
        history,
        getList,
        limit,
        status,
        roles,
        name,
        sortOrder,
        sortBy,
        route,
        key,
    } = filterHandlers;
    const query = {
        name,
        status,
        roles,
        [key]: setNewFilterValue(event, filterHandlers[key]),
        offset: 0,
        limit,
        sortOrder,
        sortBy,
    };
    
    setPage(0);
    setQuery(history, route, query);
    setFilters(query);
    getList(query);
});

